'use client';

import { SiteShellPresenter, useCurrentUser, useLocale } from '@akademi/site-builder-runtime';
import type { CurrentUser, ShellConfig } from '@akademi/site-builder-runtime';
import type { HeaderChromeProps } from '@/src/extensions/types';
import { useHeaderChrome } from '@/src/extensions/headerChromeContext';
import { usePageContent } from './PageContentOutlet';
import { VatDisplaySwitch, useVatDisplay } from '@/src/components/catalogue/VatDisplay';

const REFERENCE_SHOP_LABELS: Record<string, string[]> = {
  'Reference · Everyday Form': ['Bras', 'Underwear', 'Sleep & apparel', 'Bundles', 'Best sellers', 'New arrivals'],
  'Reference · Common House': ['Home & garden', 'Furniture', 'Electricals', 'Fashion', 'Beauty', 'Baby & child'],
  'Reference · Good Ground': ['Furniture', 'Decor', 'Dining', 'Household', 'Garden', 'Gifts'],
  'Reference · Circuit Supply': ['Cable management', 'Switches & sockets', 'Distribution', 'Lighting', 'Data networking', 'EV charging', 'Tools', 'Site supplies'],
  'Reference · Court Standard': ['Men', 'Women', 'Kids', 'Polo shirts', 'Shoes', 'Sport'],
  'Reference · Atelier Plain': ['New arrivals', 'Footwear', 'Clothing', 'Accessories', 'Permanent collection'],
  'Reference · Room Stories': ['Kitchens', 'Bedrooms', 'Living spaces', 'Finishing touches'],
  'Reference · Jobline': ['Power tools', 'Hand tools', 'Electrical', 'Plumbing', 'Painting', 'Fixings', 'Landscaping', 'Workwear'],
  'Reference · Fitting Works': ['Natural gas & LPG', 'Plumbing supplies', 'Pipe, tube & fittings', 'Heating & ventilation', 'Testing equipment', 'Tools & equipment', 'Electrical', 'Air conditioning'],
};

function normalizeReferenceShopLinks(props: Record<string, unknown>): Record<string, unknown> {
  const brand = props.brand as Record<string, unknown> | undefined;
  const labels = REFERENCE_SHOP_LABELS[String(brand?.name ?? '')];
  if (!labels) return props;

  const links = labels.map((label) => ({
    href: `/categories?department=${encodeURIComponent(label.toLowerCase().replaceAll(' ', '-'))}`,
    label,
  }));
  const footer = props.footer as Record<string, unknown> | undefined;
  const groups = Array.isArray(footer?.groups) ? footer.groups as Record<string, unknown>[] : [];
  const normalizedGroups = groups.map((group) => {
    if (String(group.label).toLowerCase() !== 'shop') return group;
    const existingLinks = Array.isArray(group.links) ? group.links as Record<string, unknown>[] : [];
    return { ...group, links: [...links, ...existingLinks.slice(labels.length)] };
  });

  return { ...props, footer: footer ? { ...footer, groups: normalizedGroups } : footer };
}

/**
 * The chrome block. Header, footer, rails, brand — all of it — comes from the
 * operator-authored Shell, composed by the backend and rendered here.
 *
 * <p><strong>There is deliberately no header component in this app.</strong> A
 * new chrome treatment is a `style` id registered against the runtime's shell
 * presenter and picked by the operator in the Shell Editor. Writing a
 * `StorefrontHeader.tsx` would take that choice away from the operator and put
 * it in a deploy.
 *
 * <p>A VERTICAL may still fill the shell's two chrome slots, through
 * `headerChrome` on its extension — a wide search bar, a department bar. It
 * comes from the extension rather than from here for the same reason: one
 * process serves every shop through this one component, so chrome written here
 * would reach all of them and be held off the rest by whatever their config
 * happened to say. `headerChromeFor` picks by the site's own `enabledPlugins`,
 * so a shop can only ever get its own vertical's chrome, and a shop whose
 * vertical ships none keeps the shell's header exactly as it is.
 *
 * <p>`SiteShellPresenter` owns the `<main>` element, which is why no route
 * component in this app renders one.
 *
 * <h3>What a storefront does not pass</h3>
 *
 * <p>No `currentUser` of its own and no `tenantId`. A storefront visitor is
 * anonymous — there is no session to read a user from, and handing the browser
 * a tenant id is exactly the thing this phase refuses. The presenter's
 * signed-in affordances simply do not light up.
 */
export function SiteShellBlock(props: Record<string, unknown>) {
  const pageContent = usePageContent();
  const currentUser = useCurrentUser<CurrentUser>();
  const { locale } = useLocale();
  const { trade } = useVatDisplay();
  const headerChrome = useHeaderChrome();

  const config = normalizeReferenceShopLinks(props) as unknown as ShellConfig;
  const chromeProps: HeaderChromeProps = {
    config,
    locale,
    localeFallback: config.i18n?.default ?? 'en',
  };
  const UnderHeader = headerChrome?.underHeader;
  const Search = headerChrome?.search;

  // The vertical's strip and the shopper's VAT switch share the one slot. The
  // vertical's comes first: it is navigation, while the switch is a preference
  // a trade customer sets once. Undefined when there is neither, because the
  // presenter draws the strip's border and padding for any slot at all and an
  // empty one reads as a stray rule under the header.
  const underHeader = UnderHeader ? <UnderHeader {...chromeProps} /> : null;
  const categorySlot =
    UnderHeader || trade ? (
      <>
        {underHeader}
        {trade ? (
          <div className="sf-vat-strip">
            <VatDisplaySwitch />
          </div>
        ) : null}
      </>
    ) : undefined;

  return (
    <SiteShellPresenter
      config={config}
      tenantSlug=""
      currentUser={currentUser}
      locale={locale}
      // Locale is a URL fact on this site (`/tr/...`), not a cookie: the
      // language switcher navigates, and there is no server action to set.
      setLocaleAction={() => undefined}
      headerCategorySlot={categorySlot}
      headerSearchSlot={Search ? <Search {...chromeProps} /> : undefined}
    >
      {pageContent}
    </SiteShellPresenter>
  );
}
